import type { FilterGroup } from 'types/userConfig/userConfig';
import type { ChatSidebarScopeTab } from 'utils/chat-sidebar-unified-statuses';
import { convertCustomFiltersToApiFilters } from 'utils/convert-filters-to-api';
import { isOutgoingLastMessageSendFailed } from 'utils/chat';
import type { ChatUserModel } from 'types/chat-users/chat-user';

export const SEND_FAILED_QUERY = 'sendFailed';

export const AWAITING_CUSTOMER_WF = 3;

/** מזהי ספירה ל-unread counts — שיחות ב-wf=3 שההודעה האחרונה יוצאת ולא נשלחה */
export const SEND_FAILED_COUNT_IDS: Record<ChatSidebarScopeTab, string> = {
  mine: '__acf_sf_mine__',
  team: '__acf_sf_team__',
  all: '__acf_sf_all__',
};

import { OUTGOING_NON_FAILURE_STATUSES } from 'utils/whatsappErrors';

export function isSendFailedQueryActive(search: string): boolean {
  const q = search.startsWith('?') ? search.slice(1) : search;
  const sp = new URLSearchParams(q);
  const v = sp.get(SEND_FAILED_QUERY);
  return v === '1' || v === 'true';
}

/** פעיל רק בתצוגת «ממתינה לאיש קשר» (wf=3) */
export function isAwaitingCustomerSendFailedFilterActive(search: string): boolean {
  const q = search.startsWith('?') ? search.slice(1) : search;
  const sp = new URLSearchParams(q);
  const wf = sp.get('wf');
  return wf === String(AWAITING_CUSTOMER_WF) && isSendFailedQueryActive(search);
}

export function buildSendFailedFilterGroups(): FilterGroup[] {
  return [
    {
      id: 'acf-sf-group',
      logic: 'and',
      conditions: [
        {
          id: 'acf-sf-direction',
          field: 'lastMessageObject.direction',
          operator: 'eq',
          value: 'outgoing',
        },
        {
          id: 'acf-sf-status',
          field: 'lastMessageObject.status',
          operator: 'notin',
          value: [...OUTGOING_NON_FAILURE_STATUSES],
        },
      ],
    },
  ];
}

export function sendFailedCountIdForScope(scope: ChatSidebarScopeTab): string {
  return SEND_FAILED_COUNT_IDS[scope];
}

export function buildSendFailedCountFiltersForScope(
  scope: ChatSidebarScopeTab,
  currentUserUdid: string,
): ReturnType<typeof convertCustomFiltersToApiFilters> {
  const base = buildSendFailedFilterGroups()[0]!;
  const conditions = [...base.conditions, { id: 'acf-wf', field: 'flowStatusCode', operator: 'eq' as const, value: AWAITING_CUSTOMER_WF }];

  if (scope === 'mine') {
    conditions.push({ id: 'acf-mine', field: 'managerId', operator: 'eq', value: currentUserUdid });
  } else if (scope === 'team') {
    conditions.push(
      { id: 'acf-team-ne', field: 'managerId', operator: 'ne', value: currentUserUdid },
      { id: 'acf-team-assigned', field: 'managerId', operator: 'isnotnull' },
    );
  }

  return convertCustomFiltersToApiFilters([{ id: 'acf-sf-scope-group', logic: 'and', conditions }], 'and');
}

export function appendSendFailedToViewUrl(url: string): string {
  const qIdx = url.indexOf('?');
  const pathname = qIdx >= 0 ? url.slice(0, qIdx) : url;
  const sp = new URLSearchParams(qIdx >= 0 ? url.slice(qIdx + 1) : '');
  sp.set('wf', String(AWAITING_CUSTOMER_WF));
  sp.set(SEND_FAILED_QUERY, '1');
  const qs = sp.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export function stripSendFailedFromViewUrl(url: string): string {
  const qIdx = url.indexOf('?');
  if (qIdx < 0) return url;
  const pathname = url.slice(0, qIdx);
  const sp = new URLSearchParams(url.slice(qIdx + 1));
  sp.delete(SEND_FAILED_QUERY);
  const qs = sp.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

/** גיבוי סינון בצד לקוח */
export function matchesAwaitingCustomerSendFailedView(user: ChatUserModel): boolean {
  return isOutgoingLastMessageSendFailed(user);
}
